import axios from "axios";
import SafeArea from "components/safearea";
import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { dollarFormatter } from "utils";
import { RiCheckboxCircleFill, RiCloseCircleFill  } from "react-icons/ri";
import { Button } from "components/button";
import PaymentForm from "./payment-form";
import { useTranslation } from 'react-i18next';
import Spinner from "components/spinner";
import { TDepositRequest } from "./types/index";
import { useModal } from "contexts/modalContext";

const getDepositStatus = (data: TDepositRequest) => {
  const remaining = data.amount_requested - (data.amount_received || 0);

  if (data.declined) {
    return "declined";
  }
  if (data.is_voided) {
    return "voided";
  }
  if (data.status === "Paid" || remaining <= 0) {
    return "succeeded";
  }
  return null;
};

export default function DepositRequest(props: any) {
  const { t } = useTranslation();
  const tBase = "pages.unprotected.deposit-request.index";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const { setShow } = useModal();

  const [stripeAccountId, setStripeAccountId] = useState<string>("");
  const [customAmount, setCustomAmount] = useState<number | null>(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [disabledInput, setDisabledInput] = useState(false);
  const [depositRequest, setDepositRequest] = useState<TDepositRequest | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const markAlreadyPaid = () => {
    setShowPaymentForm(false);
    setDisabledInput(false);
    setStatus("already_paid");
  };

  const paymentId = props.match.params.id;

  const { isLoading, refetch } = useQuery(
    ["deposit", paymentId],
    async () =>
      await axios
        .get(`${process.env.REACT_APP_API}/api/v1/consumer/deposit-request/${paymentId}`)
        .then((res) => {
          const data = res.data.deposit_request;
          const statusString = getDepositStatus(data);
          if (statusString) {
            setStatus(statusString);
          }
          setDepositRequest(data);
          setCustomAmount((prev) =>
            prev ?? data.amount_requested - (data.amount_received || 0)
          );
          return data;
        })
        .catch((err) => { 
          console.error("Error fetching deposit request:", err);
          throw new Error("Failed to fetch deposit request");
        })
  );
  
  const declinePayment = useMutation(
    ["declinePayment", paymentId],
    async ({ note }: { note: string }) => {
      return await axios
        .post(
          `${process.env.REACT_APP_API}/api/v1/consumer/deposit-request/decline/${paymentId}`, { note }
        )
        .then((res) => {
          return res.data;
        })
        .catch((err) => {
          console.error("Error declining payment:", err);
          throw new Error("Failed to decline payment");
        });
    },
    {
      onSuccess: () => {
        setShow(null);
        setStatus("declined");
      },
    }
  );

  const getStripeAccountId = async (paymentId: number) => {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/api/v1/consumer/deposit-request/company-stripe-id/${paymentId}`
    );
    return res.data;
  };

  useEffect(() => {
    getStripeAccountId(paymentId).then((data: string) => {
      setStripeAccountId(data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status !== null) {
    if (status === "declined") {
      return (
        <SafeArea padding style={{ maxWidth: "600px" }}>
          <div className="space-y-8 p-4 min-h-[400px] flex flex-col justify-center items-center text-center">
            <RiCloseCircleFill  className="text-7xl text-red-600" />
            <h1>{tr("Payment Declined")}</h1>
            <p className="max-w-prose">
              {tr("The payment has been declined. Please contact support for further assistance.")}
            </p>
            <Button primary onClick={() => window.close()}>
              {tr("Close Page")}
            </Button>
          </div>
        </SafeArea>
      );
    } else if (status === "succeeded") {
      return (
        <SafeArea padding style={{ maxWidth: "600px" }}>
          <div className="space-y-8 p-4 min-h-[400px] flex flex-col justify-center items-center text-center">
            <RiCheckboxCircleFill className="text-7xl text-green-600" />
            <h1>{tr("Payment Succeeded")}</h1>
            <p className="max-w-prose">
              {tr("Thank you for your payment. You will receive a receipt in your inbox.")}
            </p>
            <Button primary onClick={() => window.close()}>
              {tr("Close Page")}
            </Button>
          </div>
        </SafeArea>
      );
    } else if (status === "already_paid") {
      return (
        <SafeArea padding style={{ maxWidth: "600px" }}>
          <div className="space-y-8 p-4 min-h-[400px] flex flex-col justify-center items-center text-center">
            <RiCheckboxCircleFill className="text-7xl text-green-600" />
            <h1>{tr("Already Paid")}</h1>
            <p className="max-w-prose">
              {tr("This deposit request has already been paid. Please close this window.")}
            </p>
            <Button primary onClick={() => window.close()}>
              {tr("Close Page")}
            </Button>
          </div>
        </SafeArea>
      );
    } else if (status === "voided") {
      return (
        <SafeArea padding style={{ maxWidth: "600px" }}>
          <div className="space-y-8 p-4 min-h-[400px] flex flex-col justify-center items-center text-center">
            <RiCloseCircleFill  className="text-7xl text-red-600" />
            <h1>{tr("Payment Voided")}</h1>
            <p className="max-w-prose">
              {tr("The payment has been voided. Please contact support for further assistance.")}
            </p>
            <Button primary onClick={() => window.close()}>
              {tr("Close Page")}
            </Button>
          </div>
        </SafeArea>
      );
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-8 p-4 min-h-[400px] flex flex-col justify-center items-center text-center text-secondary">
        <h1 className="text-2xl ml-2">
          {tr("Loading")}
          <span className="animate-ellipsis [animation-delay:-0.9s]">.</span>
          <span className="animate-ellipsis [animation-delay:-0.6s]">.</span>
          <span className="animate-ellipsis [animation-delay:-0.3s]">.</span>
        </h1>
        <Spinner size={40} className="text-gray-400" />
      </div>
    );
  }

  if (!stripeAccountId || !depositRequest) return null;

  const data = depositRequest;
  const remaining = depositRequest
    ? depositRequest.amount_requested - (depositRequest.amount_received || 0)
    : 0;

  // Used for the "Pay" button — re-check server state before opening Stripe
  const handlePayButton = async () => {
    if (customAmount && customAmount <= 99) {
      setErrorMessage("Please enter at least $1.00.");
      setShowPaymentForm(false);
      setDisabledInput(false);
      return;
    }

    if (customAmount != null && customAmount > remaining) {
      setErrorMessage("You cannot pay more than the total amount requested.");
      setShowPaymentForm(false);
      setDisabledInput(false);
      return;
    }

    if (showPaymentForm) {
      setShowPaymentForm(false);
      setDisabledInput(false);
      return;
    }

    try {
      setIsRefreshing(true);
      setErrorMessage(null);
      const refreshed = await refetch();
      const latest = refreshed.data as TDepositRequest | undefined;

      if (!latest) {
        setErrorMessage("Unable to verify deposit request status. Please try again.");
        return;
      }

      const latestStatus = getDepositStatus(latest);
      if (latestStatus === "succeeded") {
        markAlreadyPaid();
        return;
      }
      if (latestStatus) {
        setShowPaymentForm(false);
        setDisabledInput(false);
        setStatus(latestStatus);
        return;
      }

      const latestRemaining = latest.amount_requested - (latest.amount_received || 0);
      if (customAmount != null && customAmount > latestRemaining) {
        setErrorMessage("You cannot pay more than the total amount requested.");
        setCustomAmount(latestRemaining);
        return;
      }

      setDisabledInput(true);
      setShowPaymentForm(true);
    } catch (err) {
      console.error("Error refreshing deposit request before pay:", err);
      setErrorMessage("Unable to verify deposit request status. Please try again.");
    } finally {
      setIsRefreshing(false);
    }
  };

  const type = data.fk_project_id ? "Project" : "Request";
  const typeId = data.fk_project_id || data.fk_request_id;

  const DeclineModal = () => {
    const [note, setNote] = useState("");
    
    return (
      <div>
        <div>
          <div className="mt-2">{tr("Please provide a reason for declining the request")}</div>
          <textarea
            className="w-full h-32 p-2 border border-gray-300 rounded"
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
            }}
            maxLength={500}
          />
        </div>
        <div className="flex items-center space-x-4 justify-end mt-6">
          <Button onClick={() => setShow()}>
            {tr("Cancel")}
          </Button>
          <Button
            primary
            disabled={!note.trim()}
            onClick={() => {
              declinePayment.mutate({ note });
              setShow();
            }}
          >
            {tr("Submit")}
          </Button>
        </div>
      </div>
    );
  };
  
  const decline = () => {
    setShow({
      label: tr("Decline Note"),
      component: <DeclineModal />
    });
  };

  return (
    <SafeArea padding style={{ maxWidth: "600px" }}>
      <div className="space-y-4">

        {/* Header Info */}
        <div>
          <div className="font-[500] text-[0.9rem] text-[#949599]">
            {tr("Payment")} #{data.payment_id} / {tr(type)} #{typeId}
          </div>
          <h3>{data.company_name}</h3>
          <h4 style={{ fontWeight: 500 }}>{tr("To")}: {data.customer.name} {data.customer.company && `(${data.customer.company})`}</h4>
        </div>

        <div className="divide-y">
          <div className="py-4">
            <h2>{tr("Deposit Request")}</h2>
          </div>
          <div className="divide-y">
            <div className="py-4">
              <div className="text-sm flex justify-between text-blue-500 font-bold">
                <div>{tr("ESTIMATE OPTIONS")}</div>
              </div>
            </div>

            {/* Estimate Options */}
            <div className="flex flex-col">
              {data.options.map((option, i) => (
                <div key={option.id} className={`py-4 ${i !== data.options.length - 1 ? "border-b  " : ""}`}>
                  <div className="flex justify-between items-center">
                    <div className="space-y-0.5">
                      <div className="font-[500] text-[13px] text-[#949599]">
                        {tr("Option")} #{option.fk_option_id} / {tr("Estimate")} #{option.estimate_id}
                      </div>
                      <div className="font-semibold">{option.label}</div>
                      <div className="text-sm italic">
                        {option.estimate_label}
                      </div>
                    </div>
                    <span>{dollarFormatter(option.amount)}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Paid Amount */}
            <div className="py-4">
              <div className="py flex justify-between">
                <span>{tr("Amount Requested")}</span>
                <span>{dollarFormatter(data.amount_requested || 0)}</span>
              </div>
              <div className="py flex justify-between">
                <span>{tr("Paid")}</span>
                <span>-{dollarFormatter(data.amount_received || 0)}</span>
              </div>
            </div>

            {/* Amount Requested */}
            <div className="border-y border-slate-400 py-4 font-semibold text-lg flex justify-between">
              <span>{tr("Due")}</span>
              <span>{dollarFormatter(remaining)}</span>
            </div>

            {/* Custom Amount Input */}
            <div className="pt-5 pb-4 flex justify-between">
              <label htmlFor="customAmount" className="pt-2">
                {tr("Enter Amount to Pay")}:
              </label>
              <input
                id="customAmount"
                disabled={true}
                value={dollarFormatter(customAmount) || ""}
              />
            </div>

            <div className="pt-4 flex justify-between">
              <div className="pt-3">
                {errorMessage && (
                  <div className="text-red-500">{tr(errorMessage)}</div>
                )}
              </div>
              <div className="flex items-center space-x-4">
                <Button onClick={decline}>
                  {tr("Decline")}
                </Button>
                <Button
                  primary={!disabledInput}
                  className="float-right"
                  disabled={isRefreshing}
                  onClick={handlePayButton}
                >
                  {disabledInput ? tr("Edit") : tr("Pay")}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/** 
         * Every time this is rendered / re-rendered it creates new Stripe call: payment_intent.created
         * This is so Stripe takes the current input value and creates a new payment intent
         *  */}
        {showPaymentForm && !errorMessage && (
          <div className="divide-y">
            <h2 className="pb-4">{tr("Payment Method")}</h2>
            <div className="py-4">
              <PaymentForm
                paymentId={paymentId}
                companyStripeAccountId={stripeAccountId}
                customAmount={customAmount}
                amountDue={remaining}
                onSuccess={() => setStatus("succeeded")}
                onAlreadyPaid={markAlreadyPaid}
              />
            </div>
          </div>
        )} 

      </div>
    </SafeArea>
  );
}
